import { supabase } from "@/integrations/supabase/client";
import { ApiService } from "@/services/api/ApiService";
import {
  TestRun,
  TestRunWithResults,
  TestCategory,
  RunElevenLabsTestParams,
  TestRunFilterOptions,
  CustomTestCategory,
  CreateCustomTestParams,
  SaveManualTestParams,
  RunFullAuditParams,
  RunFullAuditResponse,
  RunPhoneCheckParams,
  RunPhoneCheckResponse,
  AuditScenario,
} from "@/types/agent/brand-integrity";
import { Agent } from "@/types/agent/agent";

export class BrandIntegrityService {
  /**
   * Get available test categories from the edge function
   */
  static async getTestCategories(): Promise<TestCategory[]> {
    try {
      const { data, error } = await supabase.functions.invoke("genie-integrity-audit", {
        body: { action: "get-categories" },
      });

      if (error) {
        console.error("Error fetching test categories:", error);
        throw error;
      }

      return data?.categories || [];
    } catch (error) {
      console.error("Error fetching test categories:", error);
      throw error;
    }
  }

  /**
   * Run brand integrity test using ElevenLabs simulate-conversation API
   */
  static async runElevenLabsTest(params: RunElevenLabsTestParams): Promise<TestRunWithResults> {
    const { agentId, elevenlabsId, categories, suggestedTests, customTestIds } = params;

    const response = await ApiService.invoke<{ testRunId: string; status: string }>({
      resource: "brand-integrity",
      action: "run-custom-audit",
      data: { agentId, elevenlabsId, categories, suggestedTests, customTestIds },
    });

    return {
      id: response.testRunId,
      agent_id: agentId,
      status: "running",
      results: [],
    } as unknown as TestRunWithResults;
  }

  /**
   * Get brand test runs with filtering and pagination
   */
  static async getBrandTestRuns(options: TestRunFilterOptions & { agents: Agent[] }): Promise<{
    data: TestRun[];
    totalCount: number;
    page: number;
    pageSize: number;
  }> {
    const {
      page = 1,
      pageSize = 20,
      agentId,
      dateRange,
      agents,
      statusFilter = "all",
      runTypes,
      excludeRunTypes,
    } = options;

    const agentIds = agentId ? undefined : agents.map((a) => a.id);

    const response = await ApiService.invoke<{
      items: TestRun[];
      totalCount: number;
      page: number;
      pageSize: number;
    }>({
      resource: "brand-integrity",
      action: "list",
      data: { page, pageSize, agentId, agentIds, dateRange, statusFilter, runTypes, excludeRunTypes },
    });

    return {
      data: response.items ?? [],
      totalCount: response.totalCount ?? 0,
      page: response.page ?? page,
      pageSize: response.pageSize ?? pageSize,
    };
  }

  /**
   * Get detailed test run with all results
   */
  static async getTestRunDetails(testRunId: string): Promise<TestRunWithResults> {
    const response = await ApiService.invoke<{ testRun: TestRunWithResults }>({
      resource: "brand-integrity",
      action: "get",
      id: testRunId,
    });
    return response.testRun;
  }

  /**
   * Get test results for a specific test run (lazy loading)
   */
  static async getBrandTestResults(testRunId: string): Promise<any[]> {
    const response = await ApiService.invoke<{ items: any[] }>({
      resource: "brand-integrity",
      action: "list-results",
      id: testRunId,
    });
    return response.items ?? [];
  }

  /**
   * Export test results to CSV
   */
  static async exportTestResultsToCSV(testRunId: string): Promise<string> {
    try {
      const testRun = await this.getTestRunDetails(testRunId);

      const headers = [
        "Test Run ID",
        "Date",
        "Category",
        "Result",
        "Summary",
      ];

      const rows = testRun.results.map((result: any) => {
        const analysis = result.analysis_result;
        return [
          testRunId,
          new Date(result.created_at).toLocaleDateString(),
          analysis?.category_name || "Unknown",
          analysis?.passed ? "Passed" : "Failed",
          analysis?.elevenlabs_analysis?.transcript_summary || "",
        ];
      });

      const csvContent = [headers, ...rows]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
        .join("\n");

      return csvContent;
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      throw error;
    }
  }

  /**
   * Get custom test scenarios for the current user
   */
  static async getCustomTests(): Promise<CustomTestCategory[]> {
    const response = await ApiService.invoke<{ items: CustomTestCategory[] }>({
      resource: "brand-integrity",
      action: "list-scenarios",
    });
    return response.items ?? [];
  }

  /**
   * Create a new custom test scenario
   */
  static async createCustomTest(params: CreateCustomTestParams): Promise<CustomTestCategory> {
    const response = await ApiService.invoke<{ scenario: CustomTestCategory }>({
      resource: "brand-integrity",
      action: "create-scenario",
      data: {
        name: params.name,
        description: params.description,
        testPrompts: params.testPrompts,
        category: params.category,
      },
    });
    return response.scenario;
  }

  /**
   * Delete a custom test scenario
   */
  static async deleteCustomTest(testId: string): Promise<void> {
    await ApiService.invoke({
      resource: "brand-integrity",
      action: "delete-scenario",
      id: testId,
    });
  }

  /**
   * Get all test runs with results for an agent (for comprehensive export)
   */
  static async getAllTestRunsForAgent(
    agentId: string,
    dateRange?: string
  ): Promise<TestRunWithResults[]> {
    const response = await ApiService.invoke<{ items: TestRunWithResults[] }>({
      resource: "brand-integrity",
      action: "all",
      data: { agentId, dateRange },
    });
    return response.items ?? [];
  }

  // ── Full Brand Audit ─────────────────────────────────────────────

  /**
   * Kick off a Full Brand Audit for a Genie. Returns the testRunId the
   * audit will write to. Generation + execution run asynchronously on the
   * server; the frontend listens to the `brand-tests` channel for progress.
   */
  static async runFullAudit(params: RunFullAuditParams): Promise<RunFullAuditResponse> {
    return await ApiService.invoke<RunFullAuditResponse>({
      resource: "brand-integrity",
      action: "run-full-audit",
      data: {
        agentId: params.agentId,
        regenerate: params.regenerate ?? false,
        ...(params.scenariosPerCategory != null && { scenariosPerCategory: params.scenariosPerCategory }),
      },
    });
  }

  /**
   * Kick off a phone check: a few live situations that show whether the
   * Genie hands a browser visitor over (handoff) or transfers a phone caller
   * to the right number (transfer) when it should. Runs server-side with
   * progress on the `brand-tests` channel, like a Full Audit.
   */
  static async runPhoneCheck(params: RunPhoneCheckParams): Promise<RunPhoneCheckResponse> {
    return await ApiService.invoke<RunPhoneCheckResponse>({
      resource: "brand-integrity",
      action: `run-${params.kind}-check`,
      data: { agentId: params.agentId },
    });
  }

  /**
   * Get the AI-generated audit scenarios attached to a Full Audit run.
   * Used by the FullAuditSheet to preview what will run and by the
   * executive PDF to render the methodology section.
   */
  static async getAuditScenarios(auditRunId: number | string): Promise<AuditScenario[]> {
    const response = await ApiService.invoke<{ items: AuditScenario[] }>({
      resource: "brand-integrity",
      action: "get-audit-scenarios",
      id: String(auditRunId),
    });
    return response.items ?? [];
  }

  /**
   * Save a manual Quick Test conversation to brand integrity storage
   */
  static async saveManualTest(params: SaveManualTestParams): Promise<TestRun> {
    const response = await ApiService.invoke<{ testRun: TestRun }>({
      resource: "brand-integrity",
      action: "save-manual-test",
      data: {
        agentId: params.agentId,
        transcript: params.transcript,
        elevenlabsConversationId: params.elevenlabsConversationId,
        durationSeconds: params.durationSeconds,
      },
    });
    return response.testRun;
  }
}
